import React, { useState, useEffect } from 'react';
import { customerAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatPrice } from '../lib/utils';
import {
  Users,
  Mail,
  Phone,
  ShoppingBag,
  TrendingUp,
  Download,
  Search,
  RefreshCw,
  Loader2,
  Calendar,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [totals, setTotals] = useState({ total_customers: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total_spent'); // total_spent | order_count | last_order_date | name

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await customerAPI.list();
      setCustomers(res.data.customers || []);
      setTotals({
        total_customers: res.data.total_customers || 0,
        total_revenue: res.data.total_revenue || 0,
      });
    } catch (e) {
      console.error(e);
      toast.error('Kon klanten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, []);

  // Compute filtered + sorted list inline (small dataset, no memoization needed)
  const q = search.trim().toLowerCase();
  const filtered = (!q
    ? customers
    : customers.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q)
      )
  )
    .slice()
    .sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'last_order_date') return (b.last_order_date || '').localeCompare(a.last_order_date || '');
      if (sortBy === 'order_count') return (b.order_count || 0) - (a.order_count || 0);
      return (b.total_spent || 0) - (a.total_spent || 0);
    });

  const handleExport = async () => {
    try {
      // Fetch as authenticated blob — same as the api axios instance, but as blob
      const token = localStorage.getItem('restaurant_token');
      const res = await fetch(customerAPI.exportCsvUrl(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      // Read filename from Content-Disposition or fall back
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `foodflows-klanten-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
      toast.success('CSV gedownload');
    } catch (e) {
      console.error(e);
      toast.error('Kon CSV niet downloaden');
    }
  };

  const handleEmailAll = () => {
    if (filtered.length === 0) return;
    // Use BCC to protect customer privacy (others can't see addresses)
    const emails = filtered.map(c => c.email).filter(Boolean);
    if (emails.length === 0) {
      toast.error('Geen e-mailadressen om naar te mailen');
      return;
    }
    // mailto BCC list — clients vary in supported max length; chunk if too big
    const MAX = 100;
    if (emails.length > MAX) {
      toast.message(`Veel klanten (${emails.length}) — eerste ${MAX} worden geopend in mail-client. Gebruik CSV-export voor de volledige lijst.`);
    }
    const subject = encodeURIComponent('Nieuws van uw restaurant');
    const body = encodeURIComponent('Beste klant,\n\n[Schrijf hier uw promo-bericht]\n\nMet vriendelijke groet,');
    const bcc = emails.slice(0, MAX).join(',');
    window.location.href = `mailto:?bcc=${bcc}&subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Klanten</h1>
          <p className="text-gray-500 mt-1">Beheer uw klantenlijst en stuur promotie-mails</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchCustomers} variant="outline" className="gap-2" data-testid="customers-refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Vernieuwen
          </Button>
          <Button
            onClick={handleEmailAll}
            variant="outline"
            className="gap-2"
            disabled={filtered.length === 0}
            data-testid="customers-mail-all-btn"
          >
            <Mail className="w-4 h-4" />
            Mail alle ({filtered.length})
          </Button>
          <Button
            onClick={handleExport}
            className="gap-2 bg-[#1A4D2E] hover:bg-[#143d24] text-white"
            disabled={customers.length === 0}
            data-testid="customers-export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Exporteer CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-dashboard">
          <Users className="w-8 h-8 text-[#1A4D2E] mb-2" />
          <p className="text-3xl font-bold text-gray-900" data-testid="customers-total-count">{totals.total_customers}</p>
          <p className="text-gray-500 text-sm">Unieke klanten</p>
        </div>
        <div className="card-dashboard">
          <TrendingUp className="w-8 h-8 text-emerald-600 mb-2" />
          <p className="text-3xl font-bold text-gray-900">{formatPrice(totals.total_revenue || 0, 'EUR')}</p>
          <p className="text-gray-500 text-sm">Totale omzet uit klanten</p>
        </div>
        <div className="card-dashboard">
          <ShoppingBag className="w-8 h-8 text-orange-600 mb-2" />
          <p className="text-3xl font-bold text-gray-900">
            {totals.total_customers > 0
              ? formatPrice(totals.total_revenue / totals.total_customers, 'EUR')
              : formatPrice(0, 'EUR')}
          </p>
          <p className="text-gray-500 text-sm">Gemiddeld per klant</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card-dashboard">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Zoek op naam, e-mail of telefoon..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="customers-search-input"
              />
            </div>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-gray-700 focus:ring-2 focus:ring-[#1A4D2E]/20"
            data-testid="customers-sort-select"
          >
            <option value="total_spent">Sorteer: Beste klant</option>
            <option value="order_count">Sorteer: Meeste bestellingen</option>
            <option value="last_order_date">Sorteer: Recent besteld</option>
            <option value="name">Sorteer: Naam (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card-dashboard overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#1A4D2E] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {customers.length === 0
                ? 'Nog geen klanten met e-mailadres. Zodra klanten via je webshop bestellen verschijnen ze hier.'
                : 'Geen klanten gevonden met die zoekterm'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Naam</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">E-mail</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Telefoon</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Bestellingen</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Besteed</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Laatste</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c, idx) => (
                  <tr key={c.email || idx} className="hover:bg-gray-50 transition-colors" data-testid={`customer-row-${idx}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{c.name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`mailto:${c.email}`}
                        className="text-[#1A4D2E] hover:underline inline-flex items-center gap-1.5 text-sm"
                        data-testid={`customer-mail-link-${idx}`}
                      >
                        <Mail className="w-3.5 h-3.5" />
                        {c.email}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="text-gray-700 inline-flex items-center gap-1.5 text-sm hover:text-[#1A4D2E]">
                          <Phone className="w-3.5 h-3.5" />
                          {c.phone}
                        </a>
                      ) : (
                        <span className="text-gray-300 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-medium text-gray-900">{c.order_count}</span>
                      <span className="text-gray-400 text-xs">x</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-bold text-[#1A4D2E]">{formatPrice(c.total_spent, 'EUR')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-gray-600 text-sm">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(c.last_order_date)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tip */}
      <div className="card-dashboard bg-blue-50 border-blue-100">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium">Tip — Promotie-mails versturen</p>
            <p className="text-blue-700 mt-1">
              Gebruik <b>&quot;Mail alle&quot;</b> om je standaard e-mailprogramma te openen met alle adressen in BCC (privacy-vriendelijk).
              Of exporteer de CSV en importeer die in Mailchimp, Brevo of een andere nieuwsbrief-tool voor uitgebreidere campagnes met statistieken en afmeld-links.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;
